<template>
  <v-combobox ref="combobox" class="brand-combobox-field" v-bind="mergedAttrs">
    <template v-for="(_, slotName) in $slots" :key="slotName" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData || {}" />
    </template>
  </v-combobox>
</template>

<script setup>
import { computed, useAttrs, ref } from 'vue'
import { mergeAttrs } from '@/utils/vue-attrs'

// Define props
const props = defineProps({
  variant: {
    type: String,
    default: null,
  },
})

// Get attrs
const attrs = useAttrs()

// Template ref
const combobox = ref(null)

// Define variants
const VARIANTS = {
  basic: {
    variant: 'outlined',
    bg: 'neutral0',
    color: 'blue400',
    classes: ['brand-combobox-field--basic', 'rounded-lg'],
  },
}

// Computed common attributes
const commonAttrs = computed(() => {
  const variant = VARIANTS[props.variant]
  const classes = []
  const common = {
    'hide-details': 'auto',
    menuProps: {
      rounded: 'lg',
      offset: [8, 0],
    },
  }

  return {
    ...common,
    ...variant,
    class: [
      ...classes,
      ...(variant?.classes ?? []),
    ],
  }
})

// Merge attributes properly
const mergedAttrs = computed(() => {
  return mergeAttrs(commonAttrs.value, attrs)
})

// Methods
const focus = () => {
  combobox.value?.$el.querySelector('input')?.focus()
}

const blur = () => {
  combobox.value?.$el.querySelector('input')?.blur()
}

// Expose methods for parent component access
defineExpose({
  focus,
  blur,
})

// Set inheritAttrs to false
defineOptions({
  inheritAttrs: false,
})
</script>

<script>
export default {
  inheritAttrs: false,
}
</script>

<style lang="scss" scoped>
.v-combobox.brand-combobox-field {
  &.brand-combobox-field--basic:not(.v-input--disabled) {
    // Use lighter border color matching Vue 2 style
    :deep(.v-field__outline) {
      color: rgba(0, 0, 0, 0.15) !important;
    }
    
    // Adjust border radius to match Vue 2
    :deep(.v-field) {
      border-radius: 4px !important;
    }
    
    // Ensure proper field density to match Vue 2 sizing
    :deep(.v-field__input) {
      min-height: 48px;
      padding-top: 14px;
      padding-bottom: 14px;
    }
  }
}
</style>

<style lang="scss">
// Dark mode specific styles - unscoped to ensure proper cascade
.v-theme--dark {
  .v-combobox.brand-combobox-field.brand-combobox-field--basic:not(.v-input--disabled) {
    .v-field__outline {
      color: rgb(var(--v-theme-neutral50)) !important;
    }
  }
}
</style>
